import { useCallback, useEffect, useRef, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import styled from 'styled-components';
import debounce from 'lodash/debounce';

import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs/inputText';
import { BUTTON_SIZES, BUTTON_VARIANTS, IconButton } from 'ui/button';
import { spacing, Subtitle2 } from 'ui/theme';
import { MakeCopy } from 'ui/icons/action/MakeCopy';
import { Confirm24 } from 'ui/icons/action/Confirm24';
import { Tooltip } from 'ui/tooltip/Tooltip';
import { usePanelContext } from '../../contexts/Panel';
import { useTranslations } from '../../hooks/useTranslations';

import { PanelView } from '../../types';

const UrlSection = styled.div`
  align-items: flex-start;
  display: flex;
  column-gap: ${spacing.half};
  margin-bottom: ${spacing.half};

  > *:first-child {
    flex: 1 1 auto;
  }

  > a:first-child {
    margin-right: ${spacing.half};
  }

  button {
    flex: 0 0 auto;
    white-space: nowrap;
    width: min-content;
  }
`;

export const inputId = 'cl-url-input-field';

export const testIds = {
  copyBtn: 'cl-panel-url-input-copy-btn',
  copyBtnCopiedIcon: 'cl-panel-url-input-copy-btn-copied',
  copyBtnCopyIcon: 'cl-panel-url-input-copy-btn-copy',
  inputField: inputId,
};

type Props = {
  assetUrl?: string;
  canCopy?: boolean;
};

export const UrlInput = ({ assetUrl = '', canCopy = false }: Props) => {
  const { t } = useTranslations();
  const { panelView, setPanelView, setSelectedRemoteImageNode } = usePanelContext();
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const copiedTimer = useRef<ReturnType<typeof setTimeout>>();
  const [inputUrl, setInputUrl] = useState<string>(assetUrl);
  const [urlIsValid, setUrlIsValid] = useState<boolean>(true);
  const [urlIsValidating, setUrlIsValidating] = useState<boolean>(false);
  const imageValidator = useRef<HTMLImageElement>();

  // Show 'copied' indicator, then revert after 3 seconds
  const onCopy = useCallback(() => {
    if (copiedTimer.current) {
      clearTimeout(copiedTimer.current);
    }
    setIsCopied(true);
    copiedTimer.current = setTimeout(() => {
      setIsCopied(false);
    }, 3000);
  }, []);

  // handle validation of a remote url (capture the element if result is an image)
  const handleImageValidation = async (url: string, selectedUrl: string) => {
    // Important: a useRef is used here to eliminate the possibility of a race case in resolution of
    // competing image constructors.
    // Example with independent constructors: a url is entered but is slow to resolve, the user then
    // enters a new url which resolves quickly, the original url then times out and triggers its
    // onerror handler which creates an error state on the current (valid) url.
    // Solution: by reusing the same constructor, triggering this function overwrites the onload and
    // onerror handlers each time it is called, so they only ever resolve with the latest url
    imageValidator.current = imageValidator.current || new Image();

    imageValidator.current.onload = () => {
      setUrlIsValid(true);
      setUrlIsValidating(false);
      // only set as new remote url if it is different than the last valid url
      if (url !== selectedUrl) {
        // commit the new remote url as the selected asset (capture the image rather than just the url
        // so that we do not have to reload the image each time we need information)
        setSelectedRemoteImageNode(imageValidator.current as HTMLImageElement);
        if (panelView === PanelView.RemoteUrl) {
          setPanelView(PanelView.Detail);
        }
      }
    };

    imageValidator.current.onerror = () => {
      setUrlIsValid(false);
      setUrlIsValidating(false);
    };

    imageValidator.current.src = url;
  };

  const handleInputUrlChange = useCallback(debounce(handleImageValidation, 500), []);

  // when an asset finishes loading within the detail view, update the inputUrl with the new assetUrl
  useEffect(() => {
    setInputUrl(assetUrl);
  }, [assetUrl]);

  useEffect(() => {
    return () => {
      if (copiedTimer.current) {
        clearTimeout(copiedTimer.current);
      }
    };
  }, []);

  return (
    <section>
      <Subtitle2>{t('label.imageUrl')}</Subtitle2>
      <UrlSection>
        <FormField
          hasWarning={!urlIsValid}
          inputId={inputId}
          warningText={!urlIsValid && t('errors.remoteUrl.invalidImage')}
        >
          <InputText
            autoFocus={panelView === PanelView.RemoteUrl}
            data-test={testIds.inputField}
            id={inputId}
            isLoading={urlIsValidating}
            onChange={(value: string) => {
              setInputUrl(value);
              setUrlIsValidating(true);
              handleInputUrlChange(value, assetUrl);
            }}
            placeholder={t('input.url.placeholder')}
            value={inputUrl}
          />
        </FormField>
        {canCopy && (
          <Tooltip
            tooltipContent={t(`tooltip.${isCopied ? 'copied' : 'copy'}`)}
            open={isCopied || undefined}
          >
            <CopyToClipboard text={assetUrl} onCopy={onCopy} data-test={testIds.copyBtn}>
              <IconButton
                isDisabled={!assetUrl || !urlIsValid || urlIsValidating}
                size={BUTTON_SIZES.LARGE}
                variant={BUTTON_VARIANTS.SECONDARY}
              >
                {isCopied ? (
                  <Confirm24
                    className="shape"
                    data-test={testIds.copyBtnCopiedIcon}
                    height="24"
                    isColorInherited
                    width="24"
                  />
                ) : (
                  <MakeCopy height="24" width="24" data-test={testIds.copyBtnCopyIcon} />
                )}
              </IconButton>
            </CopyToClipboard>
          </Tooltip>
        )}
      </UrlSection>
    </section>
  );
};
